import { IntegrationServices } from '@sequencehq/api'
import {
  BulkActions,
  BulkActionsPositioner,
  ScrollFade
} from '@sequencehq/core-components'
import { bulkActionTitle } from 'modules/BulkActions/bulkActionTitle'
import { useState } from 'react'
import {
  MAX_PANEL_HEIGHT_PX,
  TIMED_OUT_TITLE
} from './customerImportsInFlight.constants'
import { InFlightCustomerImport } from './customerImportsInFlight.types'
import { useImportProgress } from './useImportProgress'

export type {
  CustomerImportFailure,
  InFlightCustomerImport
} from './customerImportsInFlight.types'

const customerImportTitle = bulkActionTitle({
  verbing: 'Importing',
  past: 'imported',
  verb: 'import',
  noun: 'customer'
})

export const CustomerImportsInFlight = (props: {
  imports: InFlightCustomerImport[]
  onDismiss: (importId: string) => void
}) => {
  const [reviewingId, setReviewingId] = useState<string | null>(null)

  if (props.imports.length === 0) {
    return null
  }

  return (
    <BulkActionsPositioner>
      <ScrollFade maxHeight={MAX_PANEL_HEIGHT_PX}>
        {props.imports.map(startedImport => (
          <CustomerImportInFlight
            key={startedImport.id}
            service={startedImport.service}
            customerIds={startedImport.customerIds}
            customerNames={startedImport.customerNames ?? {}}
            startedAt={startedImport.startedAt}
            isReviewing={reviewingId === startedImport.id}
            onChangeReviewing={isReviewing =>
              setReviewingId(isReviewing ? startedImport.id : null)
            }
            onDismiss={() => props.onDismiss(startedImport.id)}
          />
        ))}
      </ScrollFade>
    </BulkActionsPositioner>
  )
}

const CustomerImportInFlight = (props: {
  service: IntegrationServices
  customerIds: string[]
  customerNames: Record<string, string>
  startedAt?: string
  isReviewing: boolean
  onChangeReviewing: (isReviewing: boolean) => void
  onDismiss: () => void
}) => {
  const { status, hasStalled, currentCount, totalCount, failedCount, actions } =
    useImportProgress(props)

  const title = customerImportTitle({
    status,
    singularAction: totalCount === 1,
    totalSuccessful: currentCount,
    totalFailed: failedCount,
    progress: `${currentCount}/${totalCount}`,
    zipStatus: 'idle'
  })

  return (
    <BulkActions
      title={hasStalled ? TIMED_OUT_TITLE : title}
      status={status}
      actions={actions}
      listTitle="Customer"
      isReviewing={props.isReviewing}
      onChangeReviewing={props.onChangeReviewing}
      cancelAction={{ available: false }}
      onDismiss={props.onDismiss}
    />
  )
}
